import { useStepper } from "~/hooks/useStepper";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { enqueueSnackbar } from "notistack";
import { getError } from "~/lib/handle-error";
import { pluralize } from "~/lib/pluralize";
import { extractEffortStats } from "../utils/extractEffortStats";
import {
  CreateSecurityPipelinePrDialogPayload,
  CreateSecurityPipelinePrDialogProps,
} from "../CreateSecurityPipelinePRDialog";
import { extractTotalEffort } from "../utils/extractTotalEffort";
import { useScope } from "~/hooks/useScope";
import {
  useCreateSecurityPipelineWorkflowMutation,
  GetInitiativesDocument,
} from "~/operations";
import { getWorkflowUrl } from "~/pages/integrations/workflow/utils";

const getMrns = (payload: CreateSecurityPipelinePrDialogPayload) => {
  const { aggregateScoresEdges, findings, initiatives } = payload;
  const isAggregateScoresContext = (aggregateScoresEdges?.length || 0) > 0;
  const isInitiativesContext = Boolean(initiatives?.length);
  const isFindingsContext = Boolean(findings?.length);

  if (isAggregateScoresContext) {
    return aggregateScoresEdges?.map(({ node }) => node?.findingMrn) ?? [];
  }

  if (isInitiativesContext) {
    return initiatives?.map(({ mrn }) => mrn) ?? [];
  }

  if (isFindingsContext) {
    return findings?.map(({ mrn }) => mrn) ?? [];
  }

  return [];
};

const getTitles = (payload: CreateSecurityPipelinePrDialogPayload) => {
  const { aggregateScoresEdges, findings, initiatives } = payload;
  const isAggregateScoresContext = (aggregateScoresEdges?.length || 0) > 0;
  const isInitiativesContext = Boolean(initiatives?.length);
  const isFindingsContext = Boolean(findings?.length);

  if (isAggregateScoresContext) {
    return aggregateScoresEdges?.map(({ node }) => node?.title) ?? [];
  }

  if (isInitiativesContext) {
    return initiatives?.map(({ title }) => title) ?? [];
  }

  if (isFindingsContext) {
    return findings?.map(({ title }) => title) ?? [];
  }
  return [];
};

interface UseCreatePullRequestParams
  extends CreateSecurityPipelinePrDialogProps {}

export function useCreatePullRequest(params: UseCreatePullRequestParams) {
  const { activeScope } = useScope();
  const { activeStep, setStep } = useStepper({
    steps: 1,
    initialStep: 0,
  });
  const { payload } = params;
  const { aggregateScoresEdges, findings, assetMrns, initiatives } = payload;
  const isAggregateScoresContext = (aggregateScoresEdges?.length || 0) > 0;
  const isInitiativesContext = Boolean(initiatives?.length);

  const mrns = getMrns(payload);
  const titles = getTitles(payload);

  const cveOrAdvisoryFindings = (findings || []).flatMap((e) =>
    e?.__typename === "CveFinding" || e?.__typename === "AdvisoryFinding"
      ? e
      : [],
  );

  const initialStats = {
    research: 0,
    scripting: 0,
    verification: 0,
    rollout: 0,
    total: 0,
  };

  const aggScoresNodes = (aggregateScoresEdges || []).flatMap(
    (agg) => agg.node ?? [],
  );
  const manualAggregateScoresEffortStats = extractEffortStats({
    findings: aggScoresNodes,
  });

  const manualFindingsEffortStats = extractEffortStats({
    findings: cveOrAdvisoryFindings.map((finding) => ({
      ...finding,
      manualEffort: finding?.spaceRiskDelta?.manualEffort,
    })),
  });

  const manualInitiativesEffortStats = extractEffortStats({
    findings: (initiatives || []).map((initiative) => ({
      manualEffort:
        initiative?.metadata?.__typename === "AggregateInitiativeMetadata"
          ? initiative.metadata.manualEffort
          : undefined,
    })),
  });

  let manualEffortStats = manualFindingsEffortStats;

  if (isAggregateScoresContext) {
    manualEffortStats = manualAggregateScoresEffortStats;
  } else if (isInitiativesContext) {
    manualEffortStats = manualInitiativesEffortStats;
  }

  const mondooAggregateScoresEffortStats = extractTotalEffort({
    findings: aggScoresNodes,
  });

  const mondooFindingsEffortStats = extractTotalEffort({
    findings: cveOrAdvisoryFindings.map((finding) => ({
      ...finding,
      effort: finding?.spaceRiskDelta?.effort || 0,
    })),
  });

  const mondooInitiativesEffortStats = extractTotalEffort({
    findings: (initiatives || []).map((initiative) => ({
      effort:
        initiative?.metadata?.__typename === "AggregateInitiativeMetadata"
          ? initiative.metadata.effort
          : 0,
    })),
  });

  const mondooContextEffortStats = isAggregateScoresContext
    ? mondooAggregateScoresEffortStats
    : isInitiativesContext
      ? mondooInitiativesEffortStats
      : mondooFindingsEffortStats;

  const mondooEffortStats = {
    ...initialStats,
    rollout: mondooContextEffortStats,
    total: mondooContextEffortStats,
  };

  const manualAndMondooDiff = manualEffortStats.total - mondooEffortStats.total;
  const manualAndMondooDiffString =
    manualAndMondooDiff > 60
      ? `${Math.floor(manualAndMondooDiff / 60)}+ ${pluralize("hour", manualAndMondooDiff)}`
      : `${manualAndMondooDiff}+ ${pluralize("minute", manualAndMondooDiff)}`;

  const { integrations } = useSecurityPipelineIntegrations();

  const [createWorkflow, { data: workflowData, loading }] =
    useCreateSecurityPipelineWorkflowMutation();

  const handleCreatePullRequestButtonClick = async () => {
    try {
      const { data: workflowData } = await createWorkflow({
        variables: {
          input: {
            ownerMrn: activeScope?.mrn || "",
            title: `Selected fix on ${mrns?.length} findings for ${titles?.join(", ")}`,
            integrationMrn: integrations?.[0]?.mrn || "",
            findingMrns: !isInitiativesContext
              ? (mrns || []).flatMap((f) => f ?? [])
              : undefined,
            initiativeMrns: isInitiativesContext
              ? (mrns || []).map((i) => i ?? "")
              : undefined,
            ...(assetMrns ? { assetMrns } : {}),
          },
        },
        ...(isInitiativesContext
          ? { refetchQueries: [GetInitiativesDocument] }
          : {}),
      });

      enqueueSnackbar(
        `Workflow ${workflowData?.createSecurityPipelineWorkflow?.workflow.title} successfully created!`,
        {
          variant: "success",
          persist: true,
          button: {
            label: "VIEW WORKFLOW",
            href: getWorkflowUrl({
              workflowMrn:
                workflowData?.createSecurityPipelineWorkflow?.workflow.mrn ||
                "",
              params: activeScope?.params,
            }),
          },
        },
      );

      setStep(1);
    } catch (e) {
      const error = getError(e);
      enqueueSnackbar(error, { variant: "error" });
    }
  };

  return {
    handleCreatePullRequestButtonClick,
    cveOrAdvisoryFindings,
    isAggregateScoresContext,
    isLoading: loading,
    activeStep,
    workflowUrl: getWorkflowUrl({
      workflowMrn:
        workflowData?.createSecurityPipelineWorkflow?.workflow.mrn || "",
      params: activeScope?.params,
    }),
    manualEffortStats,
    mondooEffortStats,
    manualAndMondooDiffString,
  };
}
